/**
 * Patient Chart Clinical tabs.
 *
 * FHIR R4 treats Encounter as the clinical event. Clinical documentation is a
 * separate concept (DocumentReference / Composition in FHIR interoperability).
 * EMRbyMD already stores notes as internal Note records with encounterId —
 * not as invented FHIR codes and not as a forced FHIR resource mapping.
 */
import { NA, encounterNumber, encounterProvider, formatDate } from "@/lib/patientChart";
import { noteDisplayStatus, visibleEncounterNotes } from "@/lib/noteApi";

/** Paper-chart palette (approved names). */
export const CHART_PAPER = {
  RED: "#C94C4C",
  WARM_RED: "#D96B54",
  ORANGE: "#E89A3C",
  MEDIUM_BLUE: "#4A7EBF",
  CORAL: "#E8B4A0",
  LIGHT_AMBER: "#E8C86A",
  YELLOW: "#E8C040",
  DARK_GREEN: "#3F6B45",
  LIGHT_YELLOW: "#F3E6A8",
  DARK_BLUE: "#2C5282",
  LIGHT_BLUE: "#8BB8DC",
  LIGHT_GREEN: "#B5D09A",
  MEDIUM_GREEN: "#8FA87A",
  PURPLE: "#B8A4D0",
  PINK: "#E8A0B4",
  GRAY: "#A8A8B0",
  LIGHT_GRAY: "#D4D4D8",
  BROWN: "#A08058",
};

export const CHART_DIVIDERS = [
  {
    id: "front-desk",
    label: "Front Desk",
    subtitle: "Demographics, Insurance, Appointments",
    idleColor: "#E8B4A0",
    activeColor: "#E8B4A0",
    folderColor: "#E8B4A0",
  },
  {
    id: "clinical",
    label: "Clinical",
    subtitle: "Clinical Summary, Notes, Results",
    idleColor: "#8FA87A",
    activeColor: "#8FA87A",
    folderColor: "#8FA87A",
  },
  {
    id: "billing",
    label: "Billing",
    subtitle: "Charges, Claims, Payments",
    idleColor: "#B8A4D0",
    activeColor: "#B8A4D0",
    folderColor: "#B8A4D0",
  },
];

export const CLINICAL_TABS = [
  { id: "summary", label: "Summary", color: "#93CABF" },
  { id: "problem-list", label: "Problem List", color: "#FA7058" },
  { id: "medical-history", label: "Medical History", color: "#FA9924" },
  { id: "surgical-history", label: "Surgical History", color: "#D96B54" },
  { id: "encounters", label: "Encounters", color: "#F6E27A" },
  { id: "appointments", label: "Appointments", color: "#96CBF4" },
  { id: "notes", label: "Notes", color: "#6AA7EC" },
  { id: "results", label: "Results", color: "#8FC9C0" },
  { id: "medications", label: "Medications", color: "#7CB372" },
  { id: "allergies", label: "Allergies", color: "#FDDC52" },
  { id: "miscellaneous", label: "Miscellaneous", color: "#B6B1B9" },
];

export const FRONT_DESK_TABS = [
  { id: "demographics", label: "Demographics", color: CHART_PAPER.BROWN },
  { id: "insurance", label: "Insurance", color: CHART_PAPER.LIGHT_GREEN },
  { id: "appointments", label: "Appointments", color: CHART_PAPER.LIGHT_BLUE },
];

export const BILLING_TABS = [
  { id: "charges", label: "Charges", color: CHART_PAPER.BROWN },
  { id: "claims", label: "Claims", color: CHART_PAPER.MEDIUM_BLUE },
  { id: "payments", label: "Payments", color: CHART_PAPER.MEDIUM_GREEN },
];

export const EMPTY_ENCOUNTER_FILTERS = {
  date: "",
  class: "",
  visitType: "",
  status: "",
  provider: "",
  location: "",
  diagnosis: "",
  search: "",
};

export const EMPTY_NOTE_FILTERS = {
  date: "",
  noteType: "",
  encounter: "",
  author: "",
  status: "",
  keyword: "",
};

export const ENCOUNTER_FILTER_LABELS = ["Date", "Class", "Visit Type", "Status", "Provider"];
export const NOTE_FILTER_LABELS = ["Date", "Note Type", "Encounter", "Author", "Status"];

/** Notes tab UI statuses. Maps existing Note/NoteVersion draft|signed values. */
export const NOTE_STATUS_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "signed", label: "Signed" },
];

export function noteStatusLabel(status) {
  return status === "signed" ? "Signed" : "Draft";
}

export function cycleClinicalTab(current, direction) {
  const index = CLINICAL_TABS.findIndex((tab) => tab.id === current);
  const next = (index + direction + CLINICAL_TABS.length) % CLINICAL_TABS.length;
  return CLINICAL_TABS[next].id;
}

export function noteDateValue(note) {
  return (
    note?.currentVersion?.signedAt ||
    note?.currentVersion?.createdAt ||
    note?.updatedAt ||
    note?.createdAt ||
    ""
  );
}

export function noteAuthorLabel(note, encounter, currentUser) {
  if (currentUser?.id && note?.originalAuthorId === currentUser.id) {
    return currentUser.displayName || currentUser.email || "You";
  }
  const signedBy = note?.currentVersion?.signedBy;
  if (currentUser?.id && signedBy === currentUser.id) {
    return currentUser.displayName || currentUser.email || "You";
  }
  const provider = encounter ? encounterProvider(encounter) : NA;
  return provider && provider !== NA ? provider : NA;
}

export function chartNoteRows(notes, encounters = [], currentUser = null) {
  const byId = new Map((encounters || []).map((enc) => [enc.id, enc]));
  return visibleEncounterNotes(notes).map((note) => {
    const encounter = byId.get(note.encounterId) || null;
    const status = noteDisplayStatus(note);
    return {
      id: note.id,
      encounterId: note.encounterId,
      date: formatDate(noteDateValue(note)),
      encounterNumber: encounter ? encounterNumber(encounter) : NA,
      noteType: note.noteType?.displayName || "Note",
      title: note.title || "",
      status: status === "signed" ? "signed" : "draft",
      statusLabel: noteStatusLabel(status),
      author: noteAuthorLabel(note, encounter, currentUser),
      note,
    };
  });
}

export function filterEncounterRows(encounters, search) {
  const q = String(search || "").trim().toLowerCase();
  if (!q) return encounters || [];
  return (encounters || []).filter((enc) =>
    [
      encounterNumber(enc),
      enc.status,
      enc.class?.display,
      enc.class?.code,
      enc.type?.[0]?.text,
      enc.type?.[0]?.coding?.[0]?.display,
      encounterProvider(enc),
      enc.location?.[0]?.location?.display,
      (enc.reasonCode || [])
        .flatMap((rc) => rc.coding || [])
        .map((c) => c.code)
        .join(" "),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(q),
  );
}

export function filterNoteRows(rows, keyword, status = "") {
  const wanted = String(status || "").trim().toLowerCase();
  const scoped =
    wanted === "draft" || wanted === "signed"
      ? (rows || []).filter((row) => row.status === wanted)
      : rows || [];
  const q = String(keyword || "").trim().toLowerCase();
  if (!q) return scoped;
  return scoped.filter((row) =>
    [row.date, row.encounterNumber, row.noteType, row.title, row.statusLabel, row.author]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(q),
  );
}
